import type { Role, User } from "./auth.types";

export type Permission =
  | "dashboard.view"
  | "contacts.view"
  | "contacts.edit"
  | "patients.intake.view"
  | "patients.vitals.edit"
  | "patients.consultation.edit"
  | "patients.history.view"
  | "patients.history.edit"
  | "catalog.view"
  | "catalog.edit"
  | "catalog.delete"
  | "medicine.buying_price.edit"
  | "billing.view"
  | "billing.edit"
  | "employees.view"
  | "employees.edit"
  | "reports.income.view"
  | "reports.stock.view"
  | "configuration.view"
  | "configuration.minimum_stock.view";

const rolePermissions: Record<Role, Permission[]> = {
  admin: [
    "dashboard.view",
    "contacts.view",
    "contacts.edit",
    "patients.intake.view",
    "patients.vitals.edit",
    "patients.consultation.edit",
    "patients.history.view",
    "patients.history.edit",
    "catalog.view",
    "catalog.edit",
    "catalog.delete",
    "medicine.buying_price.edit",
    "billing.view",
    "billing.edit",
    "employees.view",
    "employees.edit",
    "reports.income.view",
    "reports.stock.view",
    "configuration.view",
    "configuration.minimum_stock.view",
  ],
  front_desk: [
    "contacts.view",
    "contacts.edit",
    "patients.intake.view",
    "patients.vitals.edit",
    "patients.history.view",
  ],
  doctor: [
    "patients.intake.view",
    "patients.vitals.edit",
    "patients.consultation.edit",
    "patients.history.view",
    "patients.history.edit",
    "catalog.view",
    "catalog.edit",
  ],
  pharmacy: [
    "catalog.view",
    "catalog.edit",
    "catalog.delete",
    "billing.view",
    "billing.edit",
    "contacts.view",
    "contacts.edit",
    "patients.intake.view",
    "patients.history.view",
    "reports.stock.view",
    "configuration.minimum_stock.view",
  ],
  billing: [
    "billing.view",
    "billing.edit",
  ],
  lab_technician: [],
  it_staff: [],
  nurse: [],
  staff: [],
  viewer: [],
};

export const normalizeRole = (role?: string): Role => {
  const normalized = String(role || "viewer")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_") as Role;

  if (normalized === "administrator") return "admin";
  if (normalized === "clinic_admin") return "admin";
  if (normalized === "frontdesk") return "front_desk";
  if (normalized === "front_desk_coordinator") return "front_desk";
  if (normalized === "medical_practitioner") return "doctor";
  if (normalized === "physician") return "doctor";
  if (normalized === "physician_/_medical_practitioner") return "doctor";
  if (normalized === "accounts_officer") return "billing";
  if (normalized === "billing_&_accounts_officer") return "billing";
  if (normalized === "registered_nurse") return "pharmacy";
  if (normalized === "clinical_assistant") return "pharmacy";
  if (normalized === "registered_nurse_/_clinical_assistant") return "pharmacy";
  if (normalized === "clinic_administrator_/_manager") return "admin";
  if (normalized === "labtechnician") return "lab_technician";
  if (normalized === "medical_technician") return "lab_technician";
  if (normalized === "lab_&_medical_technician") return "lab_technician";
  if (normalized === "itstaff") return "it_staff";
  if (normalized === "systems_support") return "it_staff";
  if (normalized === "it_&_systems_support") return "it_staff";
  return normalized;
};

export const getUserRoles = (user?: Pick<User, "role" | "roles"> | null): Role[] => {
  if (!user) return [];
  return (user.roles?.length ? user.roles : [user.role]).map(normalizeRole);
};

export const userHasPermission = (
  user: Pick<User, "role" | "roles"> | null | undefined,
  permission: Permission | Permission[]
) => {
  const permissions = Array.isArray(permission) ? permission : [permission];
  const userPermissions = new Set(
    getUserRoles(user).flatMap((role) => rolePermissions[role] || [])
  );

  return permissions.some((item) => userPermissions.has(item));
};

export const rolesForPermission = (permission: Permission | Permission[]): Role[] => {
  const permissions = Array.isArray(permission) ? permission : [permission];
  return (Object.keys(rolePermissions) as Role[]).filter((role) =>
    permissions.some((item) => rolePermissions[role].includes(item))
  );
};
